/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */
import { Fragment, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { css, jsx } from '@emotion/react'

import { EuiSelectable, EuiText } from '@elastic/eui'

import { useConfig } from '@modules/cui/ConfigContext'
import { FormattedMessage } from '@modules/i18n'
import type { OrganizationOption } from '@modules/organization-api'
import { useGetUserMembershipsQuery } from '@modules/organization-lib/hooks'
import { OrganizationSuffix } from '@modules/organizations-user-components/OrganizationSuffix'
import { useProfile } from '@modules/profile-lib/hooks'
import { sortMemberships } from '@modules/organizations-user-components/lib'
import EmptyOrganizationsPrompt from '@modules/organizations-user-components/EmptyOrganizationsPrompt'
import OrganizationSwitchConfirmation from '@modules/organizations-user-components/OrganizationSwitchConfirmation'
import { useSwitchOrganization } from '@modules/organization-lib/hooks/useSwitchOrganization'

import { messages } from './messages'

interface OrganizationSwitchListProps {
  listStyle?: React.CSSProperties
  onlyConfirmWhenLoginEnforced?: boolean
  onSelectOrganization?: (hasSelectedOrganization: boolean) => void
  showCheckmark?: boolean
}

const OrganizationSwitchList = ({
  listStyle,
  onlyConfirmWhenLoginEnforced = false,
  showCheckmark = true,
  onSelectOrganization,
}: OrganizationSwitchListProps) => {
  const { formatMessage } = useIntl()
  const isUserConsole = useConfig('APP_NAME') === 'userconsole'
  const profile = useProfile({ enabled: isUserConsole })
  const userId = profile?.user_id
  const currentOrganizationId = profile?.organization_id

  const { data, isLoading, isError } = useGetUserMembershipsQuery({ userId })
  const sortedMemberships = useMemo(() => {
    const orgMemberships = data?.memberships ?? []
    return sortMemberships({ memberships: orgMemberships, currentOrgId: currentOrganizationId })
  }, [data?.memberships, currentOrganizationId])
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationOption | undefined>(
    undefined,
  )
  const { switchOrganization } = useSwitchOrganization()

  const shouldShowConfirmation = (selectedOrg: OrganizationOption) =>
    onlyConfirmWhenLoginEnforced ? !!selectedOrg.enforce_authentication_method : true

  const onCancelConfirmation = () => {
    setSelectedOrganization(undefined)
    onSelectOrganization?.(false)
  }

  const options = useMemo(
    () => [
      ...sortedMemberships.map(
        ({ organization_id, organization_name, domain, enforce_authentication_method }) => ({
          value: organization_id,
          label: organization_name,
          append: (
            <OrganizationSuffix
              enforceAuthenticationMethod={enforce_authentication_method}
              domain={domain}
            />
          ),
          checked:
            organization_id === currentOrganizationId
              ? 'on'
              : (undefined as 'on' | 'off' | 'mixed' | undefined),
        }),
      ),
    ],
    [sortedMemberships, currentOrganizationId],
  )
  return (
    <Fragment>
      <EuiSelectable
        listProps={{ onFocusBadge: false, isVirtualized: false }}
        css={css({
          '.euiSelectableListItem__content > svg:first-of-type': {
            display: showCheckmark ? 'block' : 'none',
          },
        })}
        aria-label={formatMessage(messages.organizationListAriaLabel)}
        isLoading={isLoading}
        loadingMessage={isLoading ? formatMessage(messages.loadingOrganizations) : undefined}
        errorMessage={
          isError ? (
            <EuiText size='s'>
              <FormattedMessage {...messages.errorLoadingOrganizations} />
            </EuiText>
          ) : undefined
        }
        emptyMessage={<EmptyOrganizationsPrompt size='s' />}
        singleSelection={true}
        options={options}
        onChange={(newOptions) => {
          const selectedOrganizationId = newOptions.find((option) => option.checked === 'on')?.value
          const selectedOrg = sortedMemberships.find(
            (organization) => organization.organization_id === selectedOrganizationId,
          )

          if (selectedOrg && !shouldShowConfirmation(selectedOrg)) {
            switchOrganization({
              organization: selectedOrg,
            })
          } else {
            setSelectedOrganization(selectedOrg)
            onSelectOrganization?.(!!selectedOrganizationId)
          }
        }}
      >
        {(list) => <span style={{ width: listStyle?.width ?? '100%', flexGrow: 0 }}>{list}</span>}
      </EuiSelectable>
      {selectedOrganization && shouldShowConfirmation(selectedOrganization) && (
        <OrganizationSwitchConfirmation
          organization={selectedOrganization}
          onCancel={onCancelConfirmation}
          onlyConfirmWhenLoginEnforced={onlyConfirmWhenLoginEnforced}
        />
      )}
    </Fragment>
  )
}

export default OrganizationSwitchList
